import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useTheme
} from '@mui/material'
import InCardStack from 'components/atoms/container/InCardStack'
import OverlayLoading from 'components/atoms/OverlayLoading'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import RowStack from 'components/atoms/container/RowStack'
import FormInputBox from 'components/atoms/input/FormInputBox'
import { useFormik } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { FEATURES_FEEDBACK_URL, FEATURE_KEYS } from 'services/constants'
import { trackEvent } from 'services/amplitudeTracking'
import colors from 'styles/colors'
import { postRequest } from 'utils/api'
import { MOBILE } from 'utils/constants'
import { getPublicUser } from 'utils/localStorage'

const optionEmojis = ['😞', '🙁', '😐', '🙂', '😀']
const EmojiIconButton = ({ emoji, selected, onClick }) => {
  return (
    <Box
      width={64}
      height={64}
      padding={4}
      borderRadius={50}
      backgroundColor={selected ? colors.GoldenYellow200 : 'unset'}>
      <IconButton
        onClick={onClick}
        sx={[
          !selected && {
            filter: 'grayscale(100%)'
          },
          {
            fontSize: 48,
            width: 48,
            height: 48,
            '&:hover': { filter: 'none' }
          }
        ]}>
        {emoji}
      </IconButton>
    </Box>
  )
}

export const RolePlayFeedback = ({
  email,
  intialScore,
  setOpen,
  rolePlayScore,
  rolePlayTitle,
  source
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(false)
  const [closed, setClosed] = useState(false)
  const [loading, setLoading] = useState(false)

  const intialEmail = email || getPublicUser()?.email
  const formik = useFormik({
    initialValues: {
      email: intialEmail,
      score: intialScore ? parseInt(intialScore) - 1 : null,
      feedback: ''
    },
    onSubmit: async values => {
      setLoading(true)
      const requestBody = {
        featureKey: FEATURE_KEYS.ROLE_PLAY_STANDALONE,
        email: values.email,
        score: values.score + 1,
        feedback: values.feedback,
        content: { source }
      }
      const res = await postRequest(FEATURES_FEEDBACK_URL, requestBody)
      if (res.status === 201) {
        setSubmitted(true)
      }
      setLoading(false)
      trackEvent('Feedback Submitted', {
        email: values.email,
        answerOption: values.score + 1,
        title: rolePlayTitle,
        feedback: values.feedback,
        score: rolePlayScore,
        source,
      })
    }
  })
  if (!intialEmail) {
    setOpen && setOpen(false)
    return null
  }
  const handleClose = () => {
    setOpen && setOpen(false)
    setClosed(true)
  }
  return closed ? null : (
    <BoxWithBackground
      backgroundColor={colors.GoldenYellow50}
      sx={{ padding: 20 }}>
      <InCardStack sx={{ width: '100%' }}>
        {submitted ? (
          <InCardStack>
            <Typography
              variant='h1'
              textAlign='center'
              sx={theme.publicRolePlay.scenario.botName}>
              {t('publicRolePlay.thankYou')}
            </Typography>
            <Typography>{t('publicRolePlay.feedbackSubmitted')}</Typography>
            <Button onClick={handleClose}>{t('publicRolePlay.close')}</Button>
          </InCardStack>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <InCardStack sx={{ maxWidth: 700, justifySelf: 'center' }}>
              <Typography
                variant='h1'
                textAlign='center'
                sx={theme.publicRolePlay.scenario.botName}>
                {t('publicRolePlay.makeAireBetter')}
              </Typography>
              <Typography textAlign='center'>
                {t('publicRolePlay.yourFeedbackIsCrucial')}
              </Typography>
              <InCardStack spacing={2}>
                <RowStack
                  spacing={0}
                  sx={{
                    justifyContent: 'space-between',
                    gap: 10,
                    [MOBILE]: { gap: 0 }
                  }}>
                  {optionEmojis.map((emoji, index) => (
                    <EmojiIconButton
                      emoji={emoji}
                      selected={index === formik.values.score}
                      onClick={() => {
                        trackEvent('Feedback Clicked', {
                          location: 'modal',
                          email: intialEmail,
                          answerOption: index + 1,
                          score: rolePlayScore,
                          title: rolePlayTitle,
                          source,
                        })
                        formik.setFieldValue('score', index)
                      }}
                      key={`emoji-option-${index}`}
                    />
                  ))}
                </RowStack>
                <Typography height={24}>
                  {formik.values.score !== null &&
                    t(
                      `publicRolePlay.feedbackDescriptions.${formik.values.score}`
                    )}
                </Typography>
              </InCardStack>
              <FormInputBox
                formik={formik}
                attributeName='feedback'
                placeholder={t('publicRolePlay.anyAdditionalFeedback')}
                multiline={true}
                rows={5}
                sx={{
                  width: '100%',
                  backgroundColor: theme.palette.background.paper
                }}
                InputProps={{
                  style: {
                    borderRadius: 10,
                    borderColor: theme.palette.primary.main
                  }
                }}
              />
              <Button
                color='secondary'
                type='submit'
                sx={{ [MOBILE]: { padding: 6 } }}
                onClick={formik.handleSubmit}
                disabled={formik.values.score === null}>
                {t('publicRolePlay.submitFeedback')}
              </Button>
            </InCardStack>
          </form>
        )}
      </InCardStack>
      <OverlayLoading open={loading} />
    </BoxWithBackground>
  )
}

export const RolePlayFeedbackPopup = () => {
  const theme = useTheme()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')?.replace(' ', '+')
  const [open, setOpen] = useState(
    Boolean(searchParams.get('feedbackOpen') === 'true' && email)
  )
  return (
    <Dialog
      open={open}
      maxWidth='xl'
      sx={theme.publicRolePlay.result.feedbackDialog}>
      <RolePlayFeedback
        email={email}
        setOpen={setOpen}
        intialScore={searchParams.get('score')}
        source='Model via Email'
      />
    </Dialog>
  )
}
